import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import BulkActionModal from '@/components/common/BulkActionModal'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { toast } from '@/hooks/useToast'

import useRemoveCourses from '../mutations/useRemoveCourses'
import type { CoursesIdsListItem } from '../queries/useCoursesIds'

type RemoveCourseModalProps = {
  courses: CoursesIdsListItem[]
  onOpenChange: (courses: CoursesIdsListItem[] | null) => void
  onRemoveSuccess?: (courses: CoursesIdsListItem[]) => void
}

const PAGE_SIZE = DEFAULT_PAGE_SIZE

const BulkRemoveCourseModal = (props: RemoveCourseModalProps) => {
  const { t } = useTranslation('coursesManagement')
  const [page, setPage] = useState(1)

  const unableToRemove = useMemo(
    () => props.courses.filter(course => !course.canBeDeleted),
    [props.courses]
  )

  const unableToRemoveCount = unableToRemove.length

  const paginatedUnableToRemove = useMemo(() => {
    const startIdx = (page - 1) * PAGE_SIZE
    const endIdx = startIdx + PAGE_SIZE

    return unableToRemove
      .slice(startIdx, endIdx)
      .map(course => ({ id: course.id, name: course.displayName }))
  }, [page, unableToRemove])

  const { mutate: removeCourse, isPending: isRemovePending } = useRemoveCourses(
    {
      onSuccess: response => {
        if (response.successfully_deleted > 0) {
          props.onRemoveSuccess?.(props.courses)
          toast({
            variant: 'success',
            title: t('toast.courses-were-removed', {
              count: response.successfully_deleted
            })
          })
        }

        if (response.unsuccessfully_deleted > 0) {
          toast({
            variant: 'error',
            title: t('toast.failed-to-remove-courses', {
              count: response.unsuccessfully_deleted
            })
          })
        }

        props.onOpenChange(null)
      },
      onError: () => {
        toast({
          variant: 'error',
          title: t('toast.courses-remove-error')
        })
      }
    }
  )

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setPage(1)
    props.onOpenChange(isOpen ? props.courses : null)
  }

  const handleRemoveClick = () =>
    removeCourse(props.courses.map(course => course.id))

  return (
    <BulkActionModal
      open
      onOpenChange={handleOpenChange}
      count={unableToRemoveCount}
      unableToPerformActionData={paginatedUnableToRemove}
      page={page}
      pageSize={PAGE_SIZE}
      onPageChange={setPage}
      title={t('header.remove-courses')}
      description={
        <>
          <p>
            {t('text.want-to-remove-courses', {
              count: props.courses.length
            })}
          </p>
          <p>{t('text.contact-with-admin')}</p>
        </>
      }
      alertTitle={t('alert.unable-to-remove-courses.title', {
        count: unableToRemoveCount
      })}
      alertDescription={t('alert.unable-to-remove-courses.description')}
      confirmButton={
        <Button
          variant="danger"
          onClick={handleRemoveClick}
          loading={isRemovePending}
        >
          {t('button.remove')}
        </Button>
      }
    />
  )
}

export default BulkRemoveCourseModal
